import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Mail, ShieldAlert, ChevronRight, Building2, Crown, UserRound } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { API, errMsg } from "@/lib/api";
import { activate, accountLabel } from "@/lib/accounts";
import { Field, TextInput } from "@/components/common/Field";
import { BrandMark } from "@/components/common/BrandMark";

const SUSPENDED = "This account is suspended. Please contact BusinessBook support.";
const ICON = { owner: Crown, employee: Building2, portal: UserRound };

function Picker({ accounts, onBack }) {
  const [msg, setMsg] = useState("");
  const pick = (i) => (accounts[i].status === "Suspended" && !accounts[i].can_pay ? setMsg(SUSPENDED) : activate(accounts, i));
  return (
    <div className="w-full min-w-0 max-w-md space-y-5 rise" data-testid="account-picker">
      <div>
        <h2 className="font-display text-3xl font-bold tracking-tight text-[#262357]">Continue as…</h2>
        <p className="text-sm text-slate-600 mt-1.5">You have more than one account. Choose where to go — you can switch later from your profile menu.</p>
      </div>
      {msg && <div data-testid="login-suspended" className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm font-medium text-amber-800">{msg}</div>}
      <div className="space-y-2.5">
        {accounts.map((a, i) => {
          const Icon = ICON[a.kind];
          return (
            <button key={i} data-testid={`continue-as-${i}`} onClick={() => pick(i)}
              className="w-full text-left flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3.5 hover:border-teal-500 hover:shadow-md transition-[border-color,box-shadow] group">
              <span className="w-10 h-10 rounded-lg bg-[#262357] text-teal-300 flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-slate-900 break-words">{accountLabel(a)}</span>
                <span className="block text-xs text-slate-600 break-words">{a.name}{a.status === "Suspended" ? " · Suspended" : a.status === "Expired" ? " · Trial ended" : ""}</span>
              </span>
              <ChevronRight className="w-4 h-4 shrink-0 text-slate-400 group-hover:translate-x-0.5 transition-transform" />
            </button>
          );
        })}
      </div>
      <button data-testid="picker-back-btn" onClick={onBack} className="text-sm text-slate-600 hover:text-slate-800">Use a different login</button>
    </div>
  );
}

function useLoginFlow() {
  const [accounts, setAccounts] = useState(null);
  const [error, setError] = useState("");
  const done = (list) => {
    if (list.length === 1 && list[0].status === "Suspended" && !list[0].can_pay) return setError(SUSPENDED);
    if (list.length === 1) return activate(list, 0);
    setAccounts(list);
  };
  return { accounts, setAccounts, error, setError, done };
}

export default function LoginPage() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { accounts, setAccounts, error, setError, done } = useLoginFlow();
  const [ident, setIdent] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [info, setInfo] = useState("");
  const magic = params.get("magic");

  useEffect(() => {
    if (!magic) return;
    setBusy(true);
    axios.post(`${API}/auth/login-link/verify`, { token: magic }).then((r) => done(r.data.accounts))
      .catch((e) => setError(errMsg(e))).finally(() => { setBusy(false); navigate("/login", { replace: true }); });
  }, [magic]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && !accounts && !magic) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo("");
    if (linkMode) {
      if (!ident.includes("@")) return setError("Enter your email address to get a login link");
      setBusy(true);
      try { const { data } = await axios.post(`${API}/auth/login-link`, { email: ident }); setInfo(data.message); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
      return;
    }
    if (!ident.trim() || !password) return setError("Invalid login details");
    setBusy(true);
    try { const { data } = await axios.post(`${API}/auth/unified-login`, { identifier: ident.trim(), password }); done(data.accounts); } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[1.05fr_1fr] bg-[#f6f8fb]">
      <div className="hidden lg:flex relative bg-[#262357] text-white p-12 flex-col justify-between overflow-hidden">
        <div className="absolute -right-32 -top-32 w-[28rem] h-[28rem] rounded-full bg-teal-400/25 blur-3xl" />
        <Link to="/" className="relative"><BrandMark light size={44} /></Link>
        <div className="relative max-w-md">
          <p className="text-[11px] uppercase tracking-[0.22em] text-teal-300 font-semibold mb-4">One login for your whole business</p>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.08] tracking-tight">Your team, customers and books — in one place.</h1>
          <p className="mt-6 text-sm text-slate-300 leading-relaxed">Administrators, employees, clients and vendors all sign in here with their mobile number or email.</p>
        </div>
        <p className="relative text-xs text-slate-400">&copy; 2026 Largish Ecommerce Pvt Ltd</p>
      </div>
      <div className="min-w-0 flex items-center justify-center p-6 sm:p-12">
        {accounts ? <Picker accounts={accounts} onBack={() => setAccounts(null)} /> : (
          <form onSubmit={submit} className="w-full max-w-sm space-y-6 rise" data-testid="login-form">
            <div className="lg:hidden"><BrandMark /></div>
            <div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-[#262357]">Log in</h2>
              <p className="text-sm text-slate-600 mt-1.5">{linkMode ? "We'll email you a one-time login link." : "Use your mobile number or email and password."}</p>
            </div>
            {error && <div data-testid="login-error" className="rounded-md border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">{error}</div>}
            {info && <div data-testid="login-info" className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2.5 text-sm font-medium text-teal-800">{info}</div>}
            <Field label={linkMode ? "Email" : "Mobile number or email"} required>
              <TextInput data-testid="login-mobile-input" type="text" autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={ident} onChange={(e) => setIdent(e.target.value)} autoFocus placeholder={linkMode ? "you@company.com" : "10 digit mobile or email"} />
            </Field>
            {!linkMode && (
              <Field label="Password" required>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <TextInput data-testid="login-password-input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" placeholder="Enter password" />
                </div>
              </Field>
            )}
            <button data-testid="login-submit-btn" type="submit" disabled={busy}
              className="w-full h-11 rounded-full bg-[#262357] text-white text-sm font-semibold hover:bg-[#34307a] active:scale-[0.99] transition-[background-color,transform] disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} {linkMode ? "Email me a login link" : "Log in"}
            </button>
            <button type="button" data-testid="login-link-toggle" onClick={() => { setLinkMode(!linkMode); setError(""); setInfo(""); }}
              className="w-full flex items-center justify-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900">
              {linkMode ? <><Lock className="w-4 h-4" /> Log in with password</> : <><Mail className="w-4 h-4" /> Email me a login link</>}
            </button>
            <p className="text-sm text-slate-600 text-center">New business? <Link to="/signup" data-testid="login-signup-link" className="font-semibold text-[#262357] hover:underline">Start a free trial</Link></p>
            <p className="text-xs text-slate-600 flex items-center gap-1.5 justify-center"><ShieldAlert className="w-3.5 h-3.5" /> Accounts lock for 15 minutes after repeated failed attempts.</p>
          </form>
        )}
      </div>
    </div>
  );
}
